import { defineStore } from 'pinia';
import subscriptionApi from '@/services/subscription_api.ts';
import {
  SubscriptionPlan,
  SchoolSubscription,
  SchoolSubscriptionResponse,
  TrialRequest,
  PlanChangeRequest,
  AutoRenewRequest,
  SchoolRegistrationRequest,
  SchoolRegistrationResponse
} from '@/types/subscription_schema.ts';
import { mapBackendError } from '@/lib/utils.ts';
import { AxiosError } from 'axios';
import { normalizeSubscription } from '@/lib/subscription-display';

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    subscriptionPlans: [] as SubscriptionPlan[],
    schoolSubscription: {} as SchoolSubscription,
    isLoadingSubscription: false,
    subscriptionError: false,
    hasExpiredTrial: false, // New state to track if user had a trial that expired
  }),
  actions: {
    async registerSchool(registration: SchoolRegistrationRequest) {
      const response = await subscriptionApi.registerSchool(registration);
      return response.data.data;
    },

    async getSubscriptionPlans() {
      const response = await subscriptionApi.getSubscriptionPlans();
      if (response.data.data != null && response.data.data.length > 0) {
        this.subscriptionPlans = response.data.data;
      }
    },

    async getSchoolSubscription() {
      // Prevent concurrent duplicate fetches
      if (this.isLoadingSubscription) return;
      this.isLoadingSubscription = true;
      this.subscriptionError = false;
      this.hasExpiredTrial = false; // Reset expired trial state
      try {
        let hasPaidSubscription = false;
        let hasTrialSubscription = false;

        // First try to get paid subscription
        try {
          const paidResponse = await subscriptionApi.getSchoolSubscription();
          if (paidResponse.data.data != null) {
            this.schoolSubscription = normalizeSubscription(paidResponse.data.data);
            if (this.schoolSubscription.is_trial) {
              hasTrialSubscription = true;
            } else {
              hasPaidSubscription = true;
            }
            return;
          }
        } catch (paidError: any) {
          // Check if it's a 404 (not found) or other error
          if (paidError.response?.status === 404) {
            console.log('No paid subscription found (404), checking for trial...');
          } else {
            console.error('Error fetching paid subscription:', paidError.response?.status || paidError.message);
            // Only 404 means absent. Auth, server, and network errors must not become Free.
            throw paidError;
          }
        }

        // If no paid subscription, check for trial subscription
        try {
          const trialResponse = await subscriptionApi.getSchoolTrialSubscription();
          if (trialResponse.data.data != null) {
            const responseData = trialResponse.data.data as SchoolSubscriptionResponse;
            this.schoolSubscription = normalizeSubscription({
              ...responseData.subscription,
              billing_info: responseData.billing_info
            });
            hasTrialSubscription = true;
            // Store trial info for future expired detection
            this.storeTrialInfo();
            return;
          }
        } catch (trialError: any) {
          // Check if it's a 404 (not found) or other error
          if (trialError.response?.status === 404) {
            console.log('No trial subscription found (404)');
            console.log(trialError)

            // Check if the error response indicates an expired trial
            // This depends on your backend API structure - you might need to adjust this
            const errorMessage = trialError.response?.data?.message || trialError.response?.data?.error || '';
            if (errorMessage === 'No active trial found' || trialError.response?.data?.code === 'TRIAL_EXPIRED') {
              // Trial existed but expired - check if backend provides plan info in error
              const expiredTrialData = trialError.response?.data?.data || {};
              console.log('Trial subscription has expired (detected from 404 error)', expiredTrialData);

              // Try to get plan info from error response or localStorage
              const previousTrialData = localStorage.getItem('previousTrialInfo');
              let planInfo = null;

              if (previousTrialData) {
                try {
                  const trialInfo = JSON.parse(previousTrialData);
                  planInfo = trialInfo.subscription_plan;
                } catch (e) {
                  console.log('Error parsing previous trial data for plan info');
                }
              }

              this.hasExpiredTrial = true;
              this.schoolSubscription = {
                id: '',
                plan: expiredTrialData.plan || null,
                status: 'expired',
                is_trial: true,
                trial_ends_at: null,
                subscription_plan: expiredTrialData.subscription_plan || planInfo || null
              } as SchoolSubscription;
              console.log('Trial subscription has expired (detected from 404 error)', this.schoolSubscription);
              return;
            }
          } else {
            console.error('Error fetching trial subscription:', trialError.response?.status || trialError.message);
            // Preserve the error state rather than inventing an inactive subscription.
            throw trialError;
          }
        }

        // If no subscription found at all, set to empty state
        if (!hasPaidSubscription && !hasTrialSubscription) {
          // Check if we previously had trial data in localStorage (indicating expired trial)
          const previousTrialData = localStorage.getItem('previousTrialInfo');
          if (previousTrialData) {
            try {
              const trialInfo = JSON.parse(previousTrialData);
              // If we had trial data before and now can't find it, likely expired
              this.hasExpiredTrial = true;
              this.schoolSubscription = {
                id: trialInfo.id || '',
                plan: trialInfo.plan || null,
                status: 'expired',
                is_trial: true,
                trial_ends_at: trialInfo.trial_ends_at || null,
                subscription_plan: trialInfo.subscription_plan || null
              } as SchoolSubscription;
              console.log('Trial subscription detected as expired from previous data');
              // Clear the previous trial data since we've processed it
              localStorage.removeItem('previousTrialInfo');
            } catch (e) {
              console.log('Error parsing previous trial data');
              this.setInactiveState();
            }
          } else {
            this.setInactiveState();
          }
        }
      } catch (error) {
        this.subscriptionError = true;
        throw error;
      } finally {
        this.isLoadingSubscription = false;
      }
    },

    setInactiveState() {
      this.schoolSubscription = {
        id: '',
        plan: null,
        status: 'inactive',
        is_trial: false,
        trial_ends_at: null,
        subscription_plan: null
      } as SchoolSubscription;
      console.log('No active subscription found - school is on free plan');
    },

    // Store trial info when we have an active trial (for future expired detection)
    storeTrialInfo() {
      if (this.schoolSubscription?.is_trial && this.schoolSubscription?.status !== 'expired') {
        const trialInfo = {
          id: this.schoolSubscription.id,
          plan: this.schoolSubscription.plan,
          trial_ends_at: this.schoolSubscription.trial_ends_at,
          subscription_plan: this.schoolSubscription.subscription_plan,
          // Store timestamp to track when this was saved
          stored_at: new Date().toISOString()
        };
        localStorage.setItem('previousTrialInfo', JSON.stringify(trialInfo));
        console.log('Stored trial info for future expired detection:', trialInfo);
      }
    },

    async getSchoolTrialSubscription(_schoolId?: string) {
      try {
        const response = await subscriptionApi.getSchoolTrialSubscription();
        if (response.data.data != null) {
          this.schoolSubscription = response.data.data;
          return response.data.data;
        }
      } catch (error) {
        throw mapBackendError(error as AxiosError);
      }
    },

    async getSchoolPaidSubscription(_schoolId?: string) {
      try {
        const response = await subscriptionApi.getSchoolSubscription();
        if (response.data.data != null) {
          this.schoolSubscription = normalizeSubscription(response.data.data);
          return response.data.data;
        }
      } catch (error) {
        throw mapBackendError(error as AxiosError);
      }
    },

    async startFreeTrial(request: TrialRequest, schoolId: number) {
      try {
        const response = await subscriptionApi.startFreeTrial(request, schoolId);
        if (response.data.data != null) {
          this.schoolSubscription = response.data.data;
          return response.data.data;
        }
      } catch (err) {
        console.log(err);
        throw mapBackendError(err as AxiosError);
      }
    },

    async changePlan(request: PlanChangeRequest) {
      const response = await subscriptionApi.changePlan(request);
      if (response.data.data != null) {
        this.schoolSubscription = response.data.data;
        return response.data.data;
      }
    },

    async updateAutoRenew(request: AutoRenewRequest) {
      const response = await subscriptionApi.updateAutoRenew(request);
      if (response.data.data != null) {
        this.schoolSubscription = response.data.data;
        return response.data.data;
      }
    },

    async verifySubscriptionActivation(schoolId: string) {
      const response = await subscriptionApi.verifySubscriptionActivation(schoolId);
      if (response.data.data != null) {
        this.schoolSubscription = response.data.data;
        return response.data.data;
      }
    }
  },
});
